import type { WorkBook } from 'xlsx';
import { matchRdStockAndItemMasterLocations } from '@erp/rpc';

import { cellText, headerIndex } from './cells';
import {
  classifyRdAccessRow,
  isRdStockAccess,
  scanRdStockWorkbook,
  type RdStockAnalysisBuckets,
  type RdStockSheetRow,
} from './rdStockAnalysis';

export const RD_STOCK_HEADERS = [
  'STOCK ACCESS COMPANY',
  'STOCK ACCESS DIVISION',
  'STOCK ACCESS LOCATION',
  'PRODUCT NUMBER',
  'STOCK RECORD ID',
] as const;

/**
 * Optional item-master location on a synthetic or paired extract. Real
 * STOCK_V workbooks only have the stock letter; the correspondence module
 * fills the partner so O is not false-unmatched against a missing C.
 */
const ITEM_MASTER_LOCATION_HEADERS = [
  'ITEM MASTER LOCATION',
  'PRODUCT ACCESS LOCATION',
  'ITEM ACCESS LOCATION',
] as const;

export type RdStockWithoutItemAnalysis = RdStockAnalysisBuckets;

export { isRdStockAccess };

function itemMasterLocationAt(row: unknown[], headerRow: unknown[]): string {
  for (const header of ITEM_MASTER_LOCATION_HEADERS) {
    const index = headerIndex(headerRow, header);
    if (index !== -1) return cellText(row[index]);
  }
  return '';
}

function itemMasterColumnLabel(headerRow: unknown[]): string {
  for (const header of ITEM_MASTER_LOCATION_HEADERS) {
    if (headerIndex(headerRow, header) !== -1) return header;
  }
  return 'ITEM MASTER LOCATION';
}

function classifyStockVRow(input: RdStockSheetRow) {
  const providedItemMaster = itemMasterLocationAt(input.row, input.headerRow);
  return classifyRdAccessRow({
    input,
    match: matchRdStockAndItemMasterLocations({
      stockLetter: input.cell('STOCK ACCESS LOCATION'),
      itemMasterLetter: providedItemMaster,
    }),
    itemColumn: itemMasterColumnLabel(input.headerRow),
    itemMasterLocation: providedItemMaster,
  });
}

/**
 * Classify STOCK_V rows through the RD DC map.
 * A known stock letter (O→C, K→A, …) is a discrepancy — the export is
 * stock with no item master. The map only names the DC for filtering.
 * Letter equality is never a match (GEN-2668).
 */
export function analyzeRdStockWithoutItem(
  workbook: WorkBook,
  fileName: string,
): RdStockWithoutItemAnalysis | null {
  return scanRdStockWorkbook({
    workbook,
    fileName,
    headers: RD_STOCK_HEADERS,
    classifyRow: classifyStockVRow,
  });
}
